import { motion } from 'framer-motion';
import { Play, ExternalLink, ClipboardCheck, FileText, IdCard, NotebookPen, PenLine, Smartphone } from 'lucide-react';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import pollWatcherThumbnail from '../assets/poll-watcher-training.png';

const STATIC_HEADER = { cta_url: '/donate' };
const STATIC_FOOTER = { contact_email: 'info@securethevotemd.com' };

const resources = [
  {
    label: 'Maryland Canvassing Overview',
    url: 'https://elections.maryland.gov/voting/canvassing.html',
  },
  {
    label: 'Challenger and Watchers Manual',
    url: 'https://elections.maryland.gov/pdf/challenger_and_watchers_manual.pdf',
  },
  {
    label: 'Rules of Conduct for Observers',
    url: 'https://elections.maryland.gov/voting/documents/Rules%20for%20Observing%20Canvasses_07242018.pdf',
  },
  {
    label: 'Canvass Challenge Form',
    url: 'https://elections.maryland.gov/voting/documents/Canvass%20Challenge%20Form_10112024.pdf',
  },
];

const essentials = [
  { label: 'Photo ID', icon: IdCard },
  { label: 'Notebook', icon: NotebookPen },
  { label: 'Pen', icon: PenLine },
  { label: 'Observer guide', icon: ClipboardCheck },
  { label: 'Copy of the manual', icon: FileText },
  { label: 'Canvass Challenge Form', icon: FileText },
  { label: 'Fully charged cell phone for documenting observations', icon: Smartphone },
];

const watchItems = [
  'Proper handling and security of original ballots',
  'Chain of custody procedures',
  'Ballot duplication and adjudication practices',
  'Transparency during ballot review',
  'Documentation of any irregularities or procedural concerns',
  'Compliance with State Board of Elections guidance',
  'Procedures for raising formal challenges',
];

const prepItems = [
  'Confirm your county\'s canvass schedule.',
  'Attend one of the refresher training sessions.',
  'Review all State Board materials in advance.',
  'Familiarize yourself with the observer checklist before arriving.',
];

const ResourceLink = ({ label, url }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4 text-slate-700 shadow-sm transition hover:border-[#9E1B32]/40 hover:shadow-md"
  >
    <span className="font-semibold group-hover:text-[#9E1B32]">{label}</span>
    <ExternalLink size={18} className="mt-0.5 flex-shrink-0 text-[#9E1B32]" />
  </a>
);

const CanvassWatchersToolkitPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="canvass-watchers-toolkit-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      <div className="relative pt-[50px]" data-testid="page-hero">
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: '#9E1B32',
            backgroundImage: "url('https://securethevotemd.com/images/2025/03/2025-Feb-25-Secure-the-Vorte-66-1024x683.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(158, 27, 50, 0.78)' }} />
        <div className="relative z-10 py-20 sm:py-28">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
            <motion.p
              className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-[#F6BF58]"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Citizen Action
            </motion.p>
            <motion.h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="page-title"
            >
              Post-Election Canvasser and Watcher Toolkit
            </motion.h1>
            <motion.p
              className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-white/90"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12 }}
            >
              How Maryland citizens and candidates can observe the 2026 Primary Election canvass.
            </motion.p>
            <motion.div
              className="w-16 h-1 bg-[#F6BF58] mx-auto mt-6"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            />
          </div>
        </div>
      </div>

      <main className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
            <motion.article
              className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-slate-600 prose-p:leading-relaxed prose-p:mb-4 prose-a:text-[#9E1B32] prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-900 prose-ul:my-4 prose-li:text-slate-600 prose-li:leading-relaxed prose-hr:my-8 prose-hr:border-slate-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="page-content"
            >
              <p className="lead text-xl text-slate-700">
                Following the discovery that more than 400,000 Maryland voters were mailed incorrect ballots during the 2026 Primary Election, public attention surrounding the post-election canvass has intensified. Citizens, candidates, and election integrity advocates across Maryland are stepping forward to observe the ballot canvassing process and ensure transparency.
              </p>

              <p>
                Fortunately, Maryland law allows members of the public to observe the canvass process.
              </p>

              <h2>You Do Not Need Permission to Attend</h2>
              <p>
                A citizen observer does not need to pre-register, complete special paperwork, or receive prior approval to observe a canvass. Simply appear at your county Board of Elections office at the designated canvass time and sign in upon arrival.
              </p>
              <p>
                Each local Board of Elections is required to publicly post the dates and times for all mail-in and provisional ballot canvasses related to the 2026 Primary Election.
              </p>
              <p>
                To locate your county's canvass schedule and learn more about the process, visit the <a href="https://elections.maryland.gov/voting/canvassing.html" target="_blank" rel="noopener noreferrer">Maryland Canvassing Overview</a>.
              </p>

              <hr />

              <h2>Candidates: Everything You Need to Know About Maryland's Ballot Canvass</h2>
              <p>
                As Maryland's post-election canvass gets underway, it is critical that candidates, challengers, and poll watchers understand when and where ballots will be reviewed — and what to watch for throughout the process.
              </p>

              <h3>The Most Important Canvass Date: Thursday, November 5, 2026</h3>
              <p>
                The most significant day of canvassing is Thursday, November 5, 2026, when counties are scheduled to canvass the original ballots.
              </p>
              <p>
                Please note that some local boards of elections have requested extensions and may conduct their original ballot canvass on different dates. Candidates and observers should check directly with their county Board of Elections to confirm the local schedule.
              </p>
              <p>
                The Maryland State Board of Elections' guidance for the original ballot canvass is available through your local Board of Elections and should be reviewed prior to attending.
              </p>

              <hr />

              <h2>Challenger and Watcher Refresher Training</h2>
              <p>
                To ensure challengers and watchers are fully prepared, the Election Integrity Committee has refresher training available.
              </p>
              <a
                href="https://www.youtube.com/watch?v=G23_KSSBft0"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Play Challenger and Watcher Refresher Training on YouTube (opens in a new tab)"
                className="not-prose group relative my-6 block overflow-hidden rounded-xl shadow-sm focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-[#9E1B32]"
              >
                <img
                  src={pollWatcherThumbnail}
                  alt="Maryland Poll Watcher Orientation, Secure the Vote Maryland"
                  width="568"
                  height="312"
                  className="block h-auto w-full"
                />
                <span aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#9E1B32] text-white shadow-lg ring-2 ring-white transition-transform group-hover:scale-110 group-focus-visible:scale-110">
                    <Play size={32} fill="currentColor" className="ml-1" />
                  </span>
                </span>
              </a>
              <p>The training session will review:</p>
              <ul>
                <li>Maryland canvassing procedures</li>
                <li>Observer rights and responsibilities</li>
                <li>Rules governing challengers and watchers</li>
                <li>Best practices for documenting concerns</li>
                <li>Procedures for submitting formal challenges</li>
              </ul>

              <hr />

              <h2>Essential Canvassing Resources</h2>
              <p>
                Before attending a canvass, every candidate, challenger, and watcher should carefully review these materials provided by the Maryland State Board of Elections.
              </p>
              <div className="not-prose grid gap-3 sm:grid-cols-2">
                {resources.map((resource) => (
                  <ResourceLink key={resource.url} {...resource} />
                ))}
              </div>

              <h2>What to Watch During the Canvass</h2>
              <p>
                Observers should pay particular attention to:
              </p>
              <ul>
                {watchItems.map((item) => <li key={item}>{item}</li>)}
              </ul>

              <p>
                Careful observation by citizens and candidates helps ensure that the canvass is conducted fairly, transparently, and in accordance with Maryland election law.
              </p>

              <h2>Before You Go</h2>
              <p>If you plan to observe a canvass, be sure to:</p>
              <ul>
                {prepItems.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <p>
                <strong>An informed observer is an effective observer.</strong> Public participation and transparency remain essential components of election integrity.
              </p>
            </motion.article>

            <motion.aside
              className="lg:sticky lg:top-28"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              data-testid="canvass-day-essentials"
            >
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
                <div className="bg-[#9E1B32] px-6 py-5 text-white">
                  <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#F6BF58]">Checklist</p>
                  <h2 className="mt-1 text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Canvass Day Essentials</h2>
                </div>
                <div className="space-y-3 p-6">
                  {essentials.map(({ label, icon: Icon }) => (
                    <div key={label} className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
                      <Icon className="mt-0.5 flex-shrink-0 text-[#9E1B32]" size={20} />
                      <span className="text-sm font-semibold leading-relaxed text-slate-700">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.aside>
          </div>
        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

export default CanvassWatchersToolkitPage;
